import React, { useState } from 'react';
import { Goal, SubGoal, Todo } from '../services/goalService';
import ConfirmDialog from './ConfirmDialog';
import TodoSummary from './TodoSummary';
import AddSubGoalModal from './AddSubGoalModal';
import LoadingSpinner from './LoadingSpinner';
import Icon from './Icon';
import { MAX_GOAL_DESCRIPTION_DISPLAY } from '../constants';
import './GoalCard.css';
import { useTranslation } from '../i18n';
import { getGoalProgress } from '../utils/goalProgress';
import { formatDate, formatPercent } from '../utils/format';
import { clickableProps } from '../utils/a11y';
import { logger } from '../utils/logger';
import { useCompletionToast } from '../hooks/useCompletionFeedback';

interface GoalCardProps {
  goal: Goal;
  onUpdateGoal: (id: string, data: Partial<Goal>) => Promise<void> | void;
  onToggleFavorite?: (id: string) => Promise<void> | void;
  onAddSubGoal: (goalId: string, title: string, description: string) => Promise<void> | void;
  onUpdateSubGoal: (goalId: string, subGoalId: string, data: Partial<SubGoal>) => Promise<void> | void;
  onDeleteSubGoal: (goalId: string, subGoalId: string) => Promise<void> | void;
  onUpdateTodo?: (subGoalId: string, todoId: string, data: Partial<Todo>) => Promise<void> | void;
  onNavigateToTasks?: () => void;
  isListView?: boolean;
  hideActions?: boolean; // Yeni prop: butonları gizlemek için
  onClick?: () => void; // Karta tıklama özelliği
}

const GoalCard: React.FC<GoalCardProps> = ({
  goal,
  onUpdateGoal,
  onToggleFavorite,
  onAddSubGoal,
  onUpdateSubGoal,
  onDeleteSubGoal,
  onUpdateTodo,
  onNavigateToTasks,
  isListView = false,
  hideActions = false,
  onClick,
}) => {
  const { t, language } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(goal.title);
  const [editDescription, setEditDescription] = useState(goal.description);
  const [editDueDate, setEditDueDate] = useState(goal.dueDate);
  const [showAddSubGoalModal, setShowAddSubGoalModal] = useState(false);
  const [deleteSubGoalId, setDeleteSubGoalId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showCompletedSubGoals, setShowCompletedSubGoals] = useState(false);
  const showCompletionToast = useCompletionToast();

  const progress = getGoalProgress(goal);
  const { subGoalsDone: completedSubGoals, subGoalsTotal: totalSubGoals, percent: progressPercentage } = progress;
  const isReadyToComplete = progress.status === 'readyToComplete';

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await onUpdateGoal(goal.id, {
        title: editTitle,
        description: editDescription,
        dueDate: editDueDate,
      });
      setIsEditing(false);
    } catch (error) {
      logger.error('Error saving goal', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddSubGoal = async (title: string, description: string) => {
    setIsLoading(true);
    try {
      await onAddSubGoal(goal.id, title, description);
      setShowAddSubGoalModal(false);
    } catch (error) {
      logger.error('Error adding subgoal', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleGoalCompletion = async () => {
    const completing = !goal.completed;
    const goalId = goal.id;
    setIsLoading(true);
    try {
      await onUpdateGoal(goalId, { completed: completing });
      // Same moment as the goal page (T-103); a failed save throws and shows its own toast.
      if (completing) {
        showCompletionToast(t('progress.goalCompleted'), () => onUpdateGoal(goalId, { completed: false }));
      }
    } catch (error) {
      logger.error('Error toggling goal', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSubGoal = async () => {
    if (deleteSubGoalId) {
      setIsLoading(true);
      try {
        await onDeleteSubGoal(goal.id, deleteSubGoalId);
        setDeleteSubGoalId(null);
      } catch (error) {
        logger.error('Error deleting subgoal', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const renderConfirmDialogs = () => (
    <>
      <ConfirmDialog
        isOpen={!!deleteSubGoalId}
        title={t('goalCard.confirm.deleteSubGoal.title')}
        message={t('goalCard.confirm.deleteSubGoal.message')}
        confirmText={t('goalCard.confirm.deleteSubGoal.confirm')}
        cancelText={t('common.cancel')}
        onConfirm={handleDeleteSubGoal}
        onCancel={() => setDeleteSubGoalId(null)}
        type="danger"
      />
    </>
  );

  // List view - simplified card with basic information
  if (isListView) {
    return (
      <>
        <div 
          className={`goal-card-modern ${goal.completed ? 'completed' : ''} ${onClick ? 'clickable' : ''} ${isLoading ? 'loading' : ''}`}
          {...(onClick ? clickableProps(() => {
            // Modal açıkken veya loading durumunda kart tıklamasını engelle
            if (deleteSubGoalId || showAddSubGoalModal || isLoading) {
              return;
            }
            onClick();
          }, 'link') : {})}
          style={onClick ? { cursor: 'pointer' } : undefined}
        >
          {isLoading && (
            <div className="goal-card-loading-overlay">
              <LoadingSpinner size="small" color="primary" hideMessage />
            </div>
          )}
          {/* Header with title and completion status */}
          <div className="goal-card-header">
            <div className="goal-title-row">
              <h3 className="goal-title-modern">{goal.title}</h3>
              <div className="goal-status-row">
                <div className="goal-status-badge">
                  {goal.completed ? (
                    <span className="status-completed">
                      <Icon name="circleCheck" className="status-icon" />
                      {t('goalCard.status.completed')}
                    </span>
                  ) : isReadyToComplete && !hideActions ? (
                    <button
                      type="button"
                      className="status-ready"
                      onClick={(e) => {
                        e.stopPropagation();
                        void toggleGoalCompletion();
                      }}
                      disabled={isLoading}
                    >
                      <Icon name="check" className="status-icon" />
                      {t('progress.completeGoalPrompt')}
                    </button>
                  ) : isReadyToComplete ? (
                    <span className="status-ready is-static">
                      <Icon name="check" className="status-icon" />
                      {t('progress.readyToComplete')}
                    </span>
                  ) : progress.status === 'notStarted' ? (
                    // Nothing done yet: neutral, not "in progress" (same as the native app).
                    <span className="status-not-started">
                      <Icon name="hourglass" className="status-icon" />
                      {t('goalCard.status.notStarted')}
                    </span>
                  ) : (
                    <span className="status-progress">
                      <Icon name="goal" className="status-icon" />
                      {t('goalCard.status.inProgress')}
                    </span>
                  )}
                </div>
                {onToggleFavorite && goal.id && !hideActions && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleFavorite(goal.id);
                    }}
                    className={`action-btn-modern favorite-header ${goal.isFavorite ? 'active' : ''}`}
                    title={goal.isFavorite ? t('goalCard.favorite.remove') : t('goalCard.favorite.add')}
                  >
                    <Icon name={goal.isFavorite ? 'star' : 'starOutline'} />
                  </button>
                )}
                {hideActions && goal.isFavorite && (
                  <span className="favorite-display">
                    <Icon name="star" />
                  </span>
                )}
              </div>
            </div>
            
            {/* Due Date */}
            {goal.dueDate && (
              <div className="goal-meta-row">
                <span className="goal-due-date-tag">
                  <Icon name="calendar" className="goal-meta-icon" />
                  {formatDate(goal.dueDate, language)}
                </span>
              </div>
            )}
          </div>

          {/* Description */}
          {goal.description && (
            <div className="goal-description-section">
              <p className="goal-description-modern">
                {goal.description.length > MAX_GOAL_DESCRIPTION_DISPLAY 
                  ? `${goal.description.substring(0, MAX_GOAL_DESCRIPTION_DISPLAY)}...` 
                  : goal.description}
              </p>
            </div>
          )}

          {/* Progress Section */}
          {totalSubGoals > 0 && (
            <div className="goal-progress-section">
              <div className="progress-info">
                <span className="progress-label">{t('goalCard.progress.label')}</span>
                <span className="progress-stats">
                  {completedSubGoals}/{totalSubGoals} {t('goalCard.progress.subGoalUnit')} • {formatPercent(progressPercentage, language)}
                </span>
              </div>
              <div className="progress-bar-modern">
                <div 
                  className="progress-fill-modern" 
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Actions - Hide if hideActions is true */}
          {!hideActions && (
            <div className="goal-actions-section">
            </div>
          )}
        </div>
        
        {/* Add SubGoal Modal */}
        <AddSubGoalModal
          isOpen={showAddSubGoalModal}
          goalTitle={goal.title}
          onAdd={handleAddSubGoal}
          onCancel={() => setShowAddSubGoalModal(false)}
        />
        
        {renderConfirmDialogs()}
      </>
    );
  }

  // Detailed view - full card with all features
  return (
    <div className={`goal-card ${goal.completed ? 'completed' : ''} ${isLoading ? 'loading' : ''}`}>
      {isLoading && (
        <div className="goal-card-loading-overlay">
          <LoadingSpinner size="small" color="primary" hideMessage />
        </div>
      )}
      <div className="goal-header">
        <div className="goal-title-section">
          {isEditing ? (
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              className="edit-input title-input"
            />
          ) : (
            <h3 className="goal-title">{goal.title}</h3>
          )}
          <div className="goal-actions">
            <button
              onClick={toggleGoalCompletion}
              className={`action-btn completion-btn ${goal.completed ? 'completed' : ''}`}
              title={goal.completed ? t('goalCard.tooltips.reopenGoal') : t('goalCard.tooltips.completeGoal')}
            >
              <span className="completion-icon">
                <Icon name={goal.completed ? 'circleCheck' : 'circle'} />
              </span>
              <span className="completion-text">
                {goal.completed ? t('goalCard.status.completed') : t('goalCard.actions.complete')}
              </span>
            </button>
            {isEditing ? (
              <>
                <button onClick={handleSave} className="action-btn save-btn">
                  <Icon name="circleCheck" />
                </button>
                <button onClick={() => setIsEditing(false)} className="action-btn cancel-btn">
                  <Icon name="close" />
                </button>
              </>
            ) : (
              <button onClick={() => setIsEditing(true)} className="action-btn edit-btn">
                <Icon name="pen" />
              </button>
            )}

          </div>
        </div>
        
        {isEditing && (
          <div className="edit-form">
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              placeholder={t('goals.goalDescription')}
              className="edit-input description-input"
            />
            <div className="edit-row">
              <input
                type="date"
                value={editDueDate}
                onChange={(e) => setEditDueDate(e.target.value)}
                className="edit-input date-input"
              />
            </div>
          </div>
        )}
      </div>

      {!isEditing && (
        <>
          {goal.description && <p className="goal-description">{goal.description}</p>}
          
          <div className="goal-meta">
            {goal.dueDate && <span className="goal-due-date">{t('goalCard.meta.dueDatePrefix')}{formatDate(goal.dueDate, language)}</span>}
          </div>

          {totalSubGoals > 0 && (
            <div className="progress-section">
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
              <span className="progress-text">{completedSubGoals}/{totalSubGoals} {t('goalCard.progress.subGoalUnit')} {t('goalCard.progress.completedSuffix')}</span>
              <span className="progress-percentage">{formatPercent(progressPercentage, language)}</span>
            </div>
          )}

          <div className="subgoals-section">
            <div className="subgoals-header">
              <h4>{t('goalCard.subGoals.title')}</h4>
              <button 
                onClick={() => setShowAddSubGoalModal(true)} 
                className="action-btn add-subgoal-btn"
              >
                <Icon name="plus" />
              </button>
            </div>

            <div className="subgoals-list">{(() => {
              const allSubGoals = goal.subGoals || [];
              const activeSubGoals = allSubGoals.filter(sg => !sg.completed);
              const completedSubGoalsList = allSubGoals.filter(sg => sg.completed);

              const renderSubGoalItem = (subGoal: SubGoal) => (
                <div key={subGoal.id} className={`subgoal-item ${subGoal.completed ? 'completed' : ''}`}>
                  <div className="subgoal-content">
                    <h5 className="subgoal-title">{subGoal.title}</h5>
                    {subGoal.description && <p className="subgoal-description">{subGoal.description}</p>}
                    
                    {/* Todo Summary - Centered */}
                    <div className="todo-summary-container">
                      <TodoSummary 
                        todos={subGoal.todos || []}
                        onUpdateTodo={(todoId, data) => {
                          onUpdateTodo && onUpdateTodo(subGoal.id, todoId, data);
                        }}
                      />
                    </div>
                  </div>
                  <div className="subgoal-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`#/subgoals/${subGoal.id}/todos`, '_blank');
                      }}
                      className="action-btn manage-todos-btn"
                      title={t('goalCard.actions.manageTodos')}
                    >
                      <Icon name="list" /> ({subGoal.todos?.length || 0})
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onUpdateSubGoal(goal.id, subGoal.id, { completed: !subGoal.completed });
                      }}
                      className={`action-btn completion-btn ${subGoal.completed ? 'completed' : ''}`}
                      title={subGoal.completed ? t('goalCard.tooltips.reopenSubGoal') : t('goalCard.tooltips.completeSubGoal')}
                    >
                      <span className="completion-icon">
                        <Icon name={subGoal.completed ? 'circleCheck' : 'circle'} />
                      </span>
                      <span className="completion-text">
                        {subGoal.completed ? t('goalCard.actions.doneShort') : t('goalCard.actions.complete')}
                      </span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteSubGoalId(subGoal.id);
                      }}
                      className="action-btn delete-subgoal-btn"
                      title={t('goalCard.actions.deleteSubGoal')}
                    >
                      <Icon name="trash" />
                    </button>
                  </div>
                </div>
              );

              return (
                <>
                  {activeSubGoals.map(renderSubGoalItem)}
                  {completedSubGoalsList.length > 0 && (
                    <div className="completed-subgoals-accordion">
                      <button
                        className="completed-subgoals-toggle"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowCompletedSubGoals(!showCompletedSubGoals);
                        }}
                      >
                        <Icon name="circleCheck" className="completed-indicator" />
                        {t('goalCard.subGoals.completedTitle')} ({completedSubGoalsList.length})
                        <Icon name={showCompletedSubGoals ? 'chevronUp' : 'chevronDown'} className="toggle-icon" />
                      </button>
                      {showCompletedSubGoals && completedSubGoalsList.map(renderSubGoalItem)}
                    </div>
                  )}
                </>
              );
            })()}
            </div>
          </div>
        </>
      )}
      
      {/* Add SubGoal Modal */}
      <AddSubGoalModal
        isOpen={showAddSubGoalModal}
        goalTitle={goal.title}
        onAdd={handleAddSubGoal}
        onCancel={() => setShowAddSubGoalModal(false)}
      />
      
      {renderConfirmDialogs()}
    </div>
  );
};

export default GoalCard;
